import type { FC, PointerEvent, ReactNode } from 'react'
import type { AxisFormat } from '@/components/charts/chart-utils'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { formatSignedPercent } from '@/lib/format'
import { ChartFrame } from '@/components/charts/chart-frame'
import { ChartTooltip, ChartTooltipRow, Swatch } from '@/components/charts/chart-tooltip'

import {
    monotonePath,
    niceScale,
    pickLabelIndexes,
    topPercent,
    useActiveIndex
} from '@/components/charts/chart-utils'

export interface LineChartPoint {
    key: string
    label: string
    tooltipLabel: string
    value: number
    previous?: number
    previousLabel?: string
}

interface LineChartProps {
    points: LineChartPoint[]
    seriesLabel: string
    previousSeriesLabel?: string
    formatValue: (value: number) => string
    ariaLabel: string
    axisFormat?: AxisFormat
    color?: string
    previousColor?: string
    height?: number
}

const VIEW = 1000

// Line chart with a crosshair tooltip. Works with mouse, touch (drag) and keyboard (arrow keys).
export const LineChart: FC<LineChartProps> = ({
    points,
    seriesLabel,
    previousSeriesLabel = 'Periode sebelumnya',
    formatValue,
    ariaLabel,
    axisFormat = 'compact',
    color = 'var(--chart-1)',
    previousColor = 'var(--chart-5)',
    height = 280
}): ReactNode => {
    const count = points.length
    const { active, setActive, onKeyDown, onFocus, onBlur } = useActiveIndex(count)
    const hasPrevious = points.some((point) => typeof point.previous === 'number')

    const scale = useMemo(() => {
        const values = points.flatMap((point) => (typeof point.previous === 'number' ? [point.value, point.previous] : [point.value]))
        return niceScale(Math.min(...values), Math.max(...values), 4)
    }, [points])

    const xPercent = (index: number): number => (count <= 1 ? 50 : (index / (count - 1)) * 100)

    const paths = useMemo(() => {
        const toPoints = (pick: (point: LineChartPoint) => number): [number, number][] => points.map((point, index) => [
            (count <= 1 ? 0.5 : index / (count - 1)) * VIEW,
            (topPercent(pick(point), scale) / 100) * VIEW
        ])

        return {
            current: monotonePath(toPoints((point) => point.value)),
            previous: hasPrevious ? monotonePath(toPoints((point) => point.previous ?? point.value)) : ''
        }
    }, [points, scale, count, hasPrevious])

    const labelIndexes = useMemo(() => pickLabelIndexes(count), [count])
    const activePoint = active === null ? null : points[active]

    const onPointer = (event: PointerEvent<HTMLDivElement>): void => {
        const rect = event.currentTarget.getBoundingClientRect()
        if (rect.width === 0) return

        const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
        setActive(Math.round(ratio * (count - 1)))
    }

    const change = activePoint && typeof activePoint.previous === 'number' && activePoint.previous !== 0
        ? ((activePoint.value - activePoint.previous) / activePoint.previous) * 100
        : null

    return (
        <div className='w-full'>
            <ChartFrame
                height={height}
                scale={scale}
                axisFormat={axisFormat}
                xAxis={labelIndexes.map((index) => (
                    <span
                        key={points[index].key}
                        className={cn(
                            'absolute top-2 text-[11px] leading-none whitespace-nowrap text-muted-foreground',
                            count <= 12 && (count - 1 - index) % 2 === 1 && 'max-sm:hidden'
                        )}
                        style={{
                            left: `${xPercent(index)}%`,
                            transform: index === 0 ? 'none' : index === count - 1 ? 'translateX(-100%)' : 'translateX(-50%)'
                        }}
                    >
                        {points[index].label}
                    </span>
                ))}
            >
                <svg
                    viewBox={`0 0 ${VIEW} ${VIEW}`}
                    preserveAspectRatio='none'
                    className='pointer-events-none absolute inset-0 size-full overflow-visible'
                    aria-hidden='true'
                >
                    {hasPrevious && (
                        <path
                            d={paths.previous}
                            fill='none'
                            stroke={previousColor}
                            strokeWidth={2}
                            strokeDasharray='5 5'
                            strokeLinecap='round'
                            vectorEffect='non-scaling-stroke'
                        />
                    )}

                    <path
                        d={paths.current}
                        fill='none'
                        stroke={color}
                        strokeWidth={2}
                        strokeLinejoin='round'
                        strokeLinecap='round'
                        vectorEffect='non-scaling-stroke'
                    />
                </svg>

                {activePoint && active !== null && (
                    <>
                        <div
                            className='pointer-events-none absolute inset-y-0 w-px bg-foreground/25'
                            style={{ left: `${xPercent(active)}%` }}
                            aria-hidden='true'
                        />

                        {hasPrevious && typeof activePoint.previous === 'number' && (
                            <span
                                className='pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card'
                                style={{ left: `${xPercent(active)}%`, top: `${topPercent(activePoint.previous, scale)}%`, background: previousColor }}
                                aria-hidden='true'
                            />
                        )}

                        <span
                            className='pointer-events-none absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card'
                            style={{ left: `${xPercent(active)}%`, top: `${topPercent(activePoint.value, scale)}%`, background: color }}
                            aria-hidden='true'
                        />

                        <ChartTooltip
                            title={activePoint.tooltipLabel}
                            x={xPercent(active)}
                            footer={change !== null && (
                                <span>
                                    Dibanding periode sebelumnya{' '}
                                    <span className='font-semibold text-foreground tabular-nums'>
                                        {formatSignedPercent(change)}
                                    </span>
                                </span>
                            )}
                        >
                            <ChartTooltipRow
                                swatch={<Swatch color={color} variant='line' />}
                                label={seriesLabel}
                                value={formatValue(activePoint.value)}
                            />

                            {hasPrevious && typeof activePoint.previous === 'number' && (
                                <ChartTooltipRow
                                    swatch={<Swatch color={previousColor} variant='dashed' />}
                                    label={activePoint.previousLabel ?? previousSeriesLabel}
                                    value={formatValue(activePoint.previous)}
                                />
                            )}
                        </ChartTooltip>
                    </>
                )}

                <div
                    role='group'
                    aria-roledescription='grafik garis'
                    aria-label={`${ariaLabel}. Gunakan tombol panah untuk membaca setiap titik.`}
                    tabIndex={0}
                    className='absolute inset-0 cursor-crosshair touch-pan-y rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
                    onPointerMove={onPointer}
                    onPointerDown={onPointer}
                    onPointerLeave={(event) => {
                        if (event.pointerType === 'mouse') setActive(null)
                    }}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
            </ChartFrame>

            <p className='sr-only' aria-live='polite'>
                {activePoint ? `${activePoint.tooltipLabel}: ${formatValue(activePoint.value)}` : ''}
            </p>
        </div>
    )
}
